import React from "react";
import { InputText } from "primereact/inputtext";
import RFG from "../../libs/inputs/RFG";
import * as ACTION from "../../services/actions";
import {
  Card,
  CardBody,
  Button,
  Container,
  Col,
  Row,
  Alert,
  Spinner
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Check, Warning, Info } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import CustIdSelector from "../../components/CustIdSelector";


export default function OTP() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const resendOtpState = useSelector((state) => state?.resedotpState);
  
  const { 
    handleSubmit, 
    control, 
    setError, 
    clearErrors,
    formState: { errors, isSubmitting } 
  } = useForm({
    defaultValues: { otp: "" },
    mode: "onChange",
  });
  
  const [timer, setTimer] = React.useState(60);
  const [resendDisabled, setResendDisabled] = React.useState(true);
  const [mobileNumber, setMobileNumber] = React.useState("");
  const [isProcessing, setIsProcessing] = React.useState(false);
  
  // Customer selector modal state
  const [showCustomerModal, setShowCustomerModal] = React.useState(false);
  const [customerData, setCustomerData] = React.useState([]);
  const [authTokens, setAuthTokens] = React.useState(null);
  
  // Loading loan details state
  const [isLoadingLoanDetails, setIsLoadingLoanDetails] = React.useState(false);
  const [loadingProgress, setLoadingProgress] = React.useState(0);
  
  // Refs to prevent double execution
  const verificationInProgressRef = React.useRef(false);
  const navigationInProgressRef = React.useRef(false);
  const loanDetailsFetchedRef = React.useRef(false);
  
  const TIMER_DURATION = 60;


  // ==================== LOAD MOBILE NUMBER ====================
  
  React.useEffect(() => {
    const mobile = localStorage.getItem("mobile");
    
    if (!mobile) {
      console.error("❌ [OTP] No mobile number found");
      navigate("/", { replace: true });
      return;
    }
    
    setMobileNumber(mobile);
    console.log("✅ [OTP] Mobile number loaded:", mobile.replace(/(\d{6})(\d{4})/, "$1****"));
  }, [navigate]);


  // ==================== TIMER MANAGEMENT ====================
  
  React.useEffect(() => {
    let intervalId;
    
    if (timer > 0) {
      intervalId = setInterval(() => {
        setTimer((prev) => {
          const newTimer = prev - 1;
          if (newTimer === 0) {
            setResendDisabled(false);
          }
          return newTimer;
        });
      }, 1000);
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [timer]);


  // ==================== CLEANUP ON UNMOUNT ====================
  
  React.useEffect(() => {
    return () => {
      verificationInProgressRef.current = false;
      navigationInProgressRef.current = false;
      loanDetailsFetchedRef.current = false;
    };
  }, []);


  // ==================== RESEND OTP ====================
  
  const handleResendOTP = React.useCallback(() => {
    if (!mobileNumber) return;
    
    console.log("📤 [OTP] Resending OTP");
    
    setTimer(TIMER_DURATION);
    setResendDisabled(true);
    
    dispatch({
      type: `${ACTION.RESEDOTP}_POST`,
      payload: { phone: mobileNumber },
      callback: (resp) => {
        if (!resp?.is_success) {
          console.error("❌ [OTP] Failed to resend");
          setTimer(0);
          setResendDisabled(false);
        } else {
          console.log("✅ [OTP] OTP resent successfully");
        }
      }
    });
  }, [mobileNumber, dispatch, TIMER_DURATION]);


  // ==================== FAILURE HANDLER ====================
  
  const handleFailedVerification = React.useCallback((response) => {
    console.log("❌ [OTP] Verification failed:", response);
    
    const errorMessage = response?.message || "Invalid OTP";
    
    setError("otp", {
      type: "manual",
      message: errorMessage
    });
  }, [setError]);


  // ==================== SUCCESS HANDLER ====================
  
  const handleSuccessfulVerification = React.useCallback((response) => {
    if (navigationInProgressRef.current) {
      console.log("⚠️ [OTP] Already processing success, ignoring duplicate");
      return;
    }


    try {
      console.log("🎉 [OTP] Processing successful verification");
      
      const { data } = response;
      const token = data?.token;
      const refreshToken = data?.refresh_token;
      const customers = data?.customer_data || [];
      
      if (!token) {
        console.error("❌ [OTP] No token in response");
        setError("otp", {
          type: "manual",
          message: "Authentication failed. Please try again."
        });
        verificationInProgressRef.current = false;
        return;
      }
      
      if (!Array.isArray(customers) || customers.length === 0) {
        console.warn("⚠️ [OTP] No customer data found");
        setError("otp", {
          type: "manual",
          message: "This number is not registered with us or no account found"
        });
        verificationInProgressRef.current = false;
        return;
      }
      
      navigationInProgressRef.current = true;
      
      // Store tokens
      localStorage.setItem("authToken", token);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
      
      // Store customer data temporarily
      localStorage.setItem("customerData", JSON.stringify(customers));
      
      // Store session info
      localStorage.setItem("sessionStartTime", Date.now().toString());
      
      // Remove mobile from temp storage
      localStorage.removeItem("mobile");
      
      console.log(`✅ [OTP] Found ${customers.length} customer(s)`);
      
      // Show customer selector
      console.log("🔍 [OTP] Showing customer selector");
      setCustomerData(customers);
      setAuthTokens(data);
      setShowCustomerModal(true);
      
    } catch (error) {
      console.error("❌ [OTP] Error handling success:", error);
      navigationInProgressRef.current = false;
      verificationInProgressRef.current = false;
      
      setError("otp", {
        type: "manual",
        message: "Session setup failed. Please try again."
      });
    }
  }, [setError]);


  // ==================== SUBMIT OTP ====================
  
  const handleOTPSubmission = React.useCallback((formData) => {
    if (verificationInProgressRef.current) {
      console.log("⚠️ [OTP] Already verifying, ignoring duplicate");
      return Promise.resolve();
    }


    if (!mobileNumber) {
      console.error("❌ [OTP] No mobile number");
      navigate("/", { replace: true });
      return Promise.resolve();
    }


    console.log("🔐 [OTP] Verifying OTP");
    
    verificationInProgressRef.current = true;
    setIsProcessing(true);
    clearErrors("otp");
    
    return new Promise((resolve) => {
      dispatch({
        type: `${ACTION.OTPVERIFY}_POST`,
        payload: { 
          username: mobileNumber, 
          password: formData.otp
        },
        callback: (resp) => {
          console.log("📨 [OTP] Response received:", resp);
          
          if (resp?.is_success === true && resp?.code === 200 && resp?.data !== null) {
            console.log("✅ [OTP] Valid response - processing success");
            handleSuccessfulVerification(resp);
          } 
          else if (resp?.is_success === false && resp?.data === null) {
            console.log("❌ [OTP] Invalid OTP detected");
            setError("otp", {
              type: "manual",
              message: "Invalid OTP"
            });
            verificationInProgressRef.current = false;
          }
          else {
            console.log("❌ [OTP] Verification failed");
            handleFailedVerification(resp);
            verificationInProgressRef.current = false;
          }
          
          setIsProcessing(false);
          resolve();
        }
      });
    });
  }, [mobileNumber, dispatch, navigate, clearErrors, setError, handleSuccessfulVerification, handleFailedVerification]);


  // ==================== FETCH LOAN DETAILS (WITH PROPER CALLBACK) ====================
  
  const fetchLoanDetails = React.useCallback(async (customerId) => {
    if (loanDetailsFetchedRef.current) {
      console.log("⚠️ [OTP] Loan details already fetched");
      return;
    }

    console.log("🚀 [OTP] Fetching loan details for customer:", customerId);
    
    loanDetailsFetchedRef.current = true;
    setIsLoadingLoanDetails(true);
    setLoadingProgress(0);
    
    // ✅ FAILSAFE: Force navigation after 10 seconds
    const failsafeTimeout = setTimeout(() => {
      console.warn("⏰ [OTP] Failsafe timeout - forcing navigation");
      setIsLoadingLoanDetails(false);
      navigate("/dashboard/", { 
        state: { 
          token: authTokens?.token,
          customerData: authTokens?.customer_data,
          authenticated: true
        },
        replace: true
      });
    }, 10000);
    
    // Progress animation
    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 150);
    
    try {
      console.log("📡 [OTP] Dispatching LOAN_DETAILS API request...");
      
      // ✅ Dispatch with callback to save to localStorage
      dispatch({
        type: `${ACTION.LOAN_DETAILS}_POST`,
        payload: {
          customer_id: customerId,
        },
        callback: (response) => {
          console.log("📨 [OTP] LOAN_DETAILS API Response:", response);
          
          // Clear intervals
          clearInterval(progressInterval);
          clearTimeout(failsafeTimeout);
          setLoadingProgress(100);
          
          // ✅ Check if response is successful
          if (response?.is_success && response?.data) {
            // ✅ Save to localStorage
            const loanKey = `Local_Loandetails${customerId}`;
            localStorage.setItem(loanKey, JSON.stringify(response));
            
            console.log(`💾 [OTP] Successfully saved to ${loanKey}`);
            console.log(`📊 [OTP] Saved ${response.data.loan_types?.length || 0} loan types`);
            console.log(`💳 [OTP] Available gateways:`, response.data.gateways);
            
            // Log account details
            if (response.data.accounts) {
              Object.keys(response.data.accounts).forEach(key => {
                console.log(`📂 [OTP] ${key}: ${response.data.accounts[key].length} account(s)`);
              });
            }
          } else {
            console.warn("⚠️ [OTP] API returned no data or failed:", response);
            console.warn("⚠️ [OTP] Response structure:", {
              is_success: response?.is_success,
              code: response?.code,
              message: response?.message,
              hasData: !!response?.data
            });
          }
          
          // Navigate to dashboard after short delay
          setTimeout(() => {
            console.log("🚀 [OTP] Navigating to dashboard...");
            setIsLoadingLoanDetails(false);
            
            navigate("/dashboard/", { 
              state: { 
                token: authTokens?.token,
                customerData: authTokens?.customer_data,
                authenticated: true
              },
              replace: true
            });
          }, 500);
        }
      });
      
    } catch (error) {
      console.error("❌ [OTP] Fetch error:", error);
      clearInterval(progressInterval);
      clearTimeout(failsafeTimeout);
      setLoadingProgress(100);
      
      // Navigate anyway on error
      setTimeout(() => {
        setIsLoadingLoanDetails(false);
        navigate("/dashboard/", { 
          state: { 
            token: authTokens?.token,
            customerData: authTokens?.customer_data,
            authenticated: true
          },
          replace: true
        });
      }, 500);
    }
    
  }, [authTokens, navigate, dispatch]);


  // ==================== CUSTOMER SELECTION HANDLER ====================
  
  const handleCustomerSelect = React.useCallback((customerId, selectedCustomerData) => {
    console.log("✅ [OTP] Customer selected:", customerId);
    console.log("📋 [OTP] Customer data:", selectedCustomerData);
    
    // Store selected customer data
    localStorage.setItem("cust_id_selected", customerId);
    localStorage.setItem("selected_customer_data", JSON.stringify(selectedCustomerData));
    
    // Close modal
    setShowCustomerModal(false);
    
    // ✅ Fetch loan details before navigating
    fetchLoanDetails(customerId);
    
  }, [fetchLoanDetails]);


  // ==================== CUSTOMER MODAL CLOSE HANDLER ====================
  
  const handleCustomerModalClose = React.useCallback(() => {
    const selectedId = localStorage.getItem("cust_id_selected");
    
    if (!selectedId) {
      const userConfirmed = window.confirm(
        "You must select a customer account to continue. " +
        "Click Cancel to go back and select, or OK to logout."
      );
      
      if (userConfirmed) {
        localStorage.clear();
        navigate("/", { replace: true });
      }
      return;
    }
    
    setShowCustomerModal(false);
  }, [navigate]);


  // ==================== RENDER ====================
  
  return (
    <>
      <Container fluid className="p-1 g-0" style={{ height: "100%" }}>
        <Row className="d-flex justify-content-center g-0">
          <Col xs={12} sm={8} md={5} xl={4}>
            <Card style={{ minWidth: 210, height: "auto", border: "none" }}>
              <CardBody className="px-4 mt-4">
                <h1 className="mb-4 app-title text-center">Payment Portal</h1>
                
                {mobileNumber && !isLoadingLoanDetails && (
                  <Alert variant="info" className="mb-4">
                    <div className="d-flex align-items-center">
                      <Info size={20} className="me-2" />
                      <div>
                        <div className="fw-bold">OTP Sent</div>
                        <small>
                          Mobile: <strong>{mobileNumber.replace(/(\d{6})(\d{4})/, "$1****")}</strong>
                        </small>
                      </div>
                    </div>
                  </Alert>
                )}
                
                <form
                  autoComplete="off"
                  onSubmit={handleSubmit(handleOTPSubmission)}
                >
                  <RFG
                    name="otp"
                    errormessage="OTP is required"
                    label="Enter OTP"
                    control={control}
                    rules={{ required: "OTP is required" }}
                    className="mb-3"
                  >
                    <InputText 
                      autoComplete="off" 
                      placeholder="Enter 6-digit OTP"
                      disabled={isProcessing || isSubmitting || isLoadingLoanDetails}
                      className={errors.otp ? "p-invalid" : ""}
                      maxLength={6}
                    />
                  </RFG>
                  
                  {errors.otp && (
                    <Alert variant="danger" className="py-2 mb-3">
                      <Warning size={16} className="me-1" />
                      {errors.otp.message}
                    </Alert>
                  )}
                  
                  <div className="text-center mb-4">
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={handleResendOTP}
                      disabled={resendDisabled || resendOtpState?.loading || isProcessing || isLoadingLoanDetails}
                    >
                      {resendOtpState?.loading 
                        ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Sending...
                          </>
                        )
                        : resendDisabled 
                          ? `Resend OTP in ${timer}s` 
                          : "Resend OTP"
                      }
                    </Button>
                  </div>


                  {(isProcessing || isSubmitting) && !isLoadingLoanDetails && (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span>Verifying OTP...</span>
                    </div>
                  )}


                  <div className="d-flex justify-content-center gap-3 mt-4">
                    <Button
                      variant="outline-danger"
                      onClick={() => {
                        localStorage.removeItem("mobile");
                        navigate("/");
                      }}
                      disabled={isProcessing || isSubmitting || isLoadingLoanDetails}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="primary"
                      type="submit"
                      disabled={isProcessing || isSubmitting || !mobileNumber || isLoadingLoanDetails}
                    >
                      {isProcessing || isSubmitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <Check size={16} className="me-1" />
                          Verify & Continue
                        </>
                      )}
                    </Button>
                  </div>
                </form>
                
                {resendOtpState?.error && !isLoadingLoanDetails && (
                  <div className="mt-3">
                    <Alert variant="warning" className="py-2">
                      <Warning size={16} className="me-1" />
                      {resendOtpState.error}
                    </Alert>
                  </div>
                )}
                
                {!isLoadingLoanDetails && (
                  <div className="text-center mt-4">
                    <small className="text-muted">
                      🔒 Your data is encrypted and secure
                    </small>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      
      {/* Customer Selector Modal */}
      <CustIdSelector
        open={showCustomerModal}
        onClose={handleCustomerModalClose}
        onSelect={handleCustomerSelect}
        customerData={customerData}
      />
      
      {/* Loading Loan Details Overlay */}
      {isLoadingLoanDetails && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999
          }}
        >
          <Card style={{ minWidth: "320px", maxWidth: "400px", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
            <CardBody className="text-center p-4">
              <Spinner animation="border" variant="primary" className="mb-3" style={{ width: "3rem", height: "3rem" }} />
              <h5 className="mb-2 fw-bold">Loading Account Details</h5>
              <p className="text-muted mb-3 small">
                Please wait while we fetch your loan information
              </p>
              
              {/* Progress Bar */}
              <div style={{ 
                width: "100%", 
                height: "8px", 
                backgroundColor: "#e9ecef", 
                borderRadius: "4px",
                overflow: "hidden",
                marginBottom: "8px"
              }}>
                <div style={{
                  width: `${loadingProgress}%`,
                  height: "100%",
                  backgroundColor: "#007bff",
                  transition: "width 0.3s ease"
                }} />
              </div>
              <small className="text-muted">
                {loadingProgress}% Complete
              </small>
            </CardBody>
          </Card>
        </div>
      )}
      
      <Footer />
    </>
  );
}
